import React, { useEffect, useRef,useState } from 'react';
import { Edit as EditIcon } from '@mui/icons-material';
import { Box, Button, Card, CardContent, Divider, Popover, Stack, TextField, Typography } from '@mui/material';

import type { BracketMatch } from '../BlockNoteBracketHighlightPlugin';

interface BracketReplacementPopoverProps {
  open: boolean;
  anchorEl: HTMLElement | null;
  bracketMatch: BracketMatch | null;
  onClose: () => void;
  onReplace: (replacementText: string) => void;
}

/**
 * Popover component for replacing bracket text
 * Appears when user clicks on a highlighted bracket
 * Styled similar to AttestationEditCard
 */
export const BracketReplacementPopover: React.FC<BracketReplacementPopoverProps> = ({
  open,
  anchorEl,
  bracketMatch,
  onClose,
  onReplace,
}) => {
  const [replacementText, setReplacementText] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  // Extract text inside brackets (remove the brackets themselves)
  const bracketContent = bracketMatch?.text ? bracketMatch.text.slice(1, -1) : '';

  // Initialize replacement text with current bracket content
  useEffect(() => {
    if (open && bracketMatch) {
      setReplacementText(bracketContent);
      // Focus input after a short delay to ensure popover is rendered
      setTimeout(() => {
        inputRef.current?.focus();
        inputRef.current?.select();
      }, 100);
    }
  }, [open, bracketMatch, bracketContent]);

  const handleReplace = () => {
    // Allow empty replacement to remove the bracket
    onReplace(replacementText);
    onClose();
  };

  const handleCancel = () => {
    setReplacementText('');
    onClose();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleReplace();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      handleCancel();
    }
  };

  return (
    <Popover
      open={open && Boolean(anchorEl)}
      anchorEl={anchorEl}
      onClose={handleCancel}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'center',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'center',
      }}
      disableRestoreFocus
      slotProps={{
        paper: {
          sx: {
            borderRadius: 2,
            border: '1px solid',
            borderColor: 'borderColors.primary',
            boxShadow: 'none',
            overflow: 'hidden',
            bgcolor: 'background.paper',
          },
        },
      }}
    >
      <Card
        sx={{
          width: 400,
          minWidth: 400,
          maxWidth: 500,
          display: 'flex',
          flexDirection: 'column',
          borderRadius: 2,
          border: 'none',
          boxShadow: 'none',
          overflow: 'hidden',
          bgcolor: 'transparent',
        }}
      >
        {/* Header Section with Icon */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 1,
            p: 1.5,
            pb: 1,
            bgcolor: 'backgroundColors.secondary',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 28,
              height: 28,
              borderRadius: 1,
              bgcolor: 'info.main',
              color: 'textColors.brandContrast',
              flexShrink: 0,
            }}
          >
            <EditIcon sx={{ fontSize: 16 }} />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: 600,
                fontSize: '0.8125rem',
                lineHeight: 1.25,
                color: 'textColors.darkText',
                mb: 0.25,
              }}
            >
              Replace Bracket Text
            </Typography>
            <Typography variant="caption" sx={{ color: 'textColors.lightText', fontSize: '0.6875rem' }}>
              Current: <code style={{ backgroundColor: '#f5f5f5', padding: '2px 4px', borderRadius: '2px' }}>{bracketMatch?.text}</code>
            </Typography>
          </Box>
        </Box>

        <Divider sx={{ borderColor: 'borderColors.primary' }} />

        {/* Content Section */}
        <CardContent sx={{ p: 1.5, pt: 1, flex: 1, display: 'flex', flexDirection: 'column' }}>
          <Stack spacing={1}>
            {/* Replacement Text Input */}
            <Box>
              <Typography
                variant="overline"
                sx={{
                  fontWeight: 600,
                  color: 'textColors.lightText',
                  fontSize: '0.625rem',
                  letterSpacing: '0.5px',
                  textTransform: 'uppercase',
                  display: 'block',
                  mb: 0.25,
                }}
              >
                Replacement Text
              </Typography>
              <TextField
                inputRef={inputRef}
                fullWidth
                size="small"
                variant="outlined"
                placeholder="Enter replacement text..."
                value={replacementText}
                onChange={e => setReplacementText(e.target.value)}
                onKeyDown={handleKeyDown}
                autoFocus
                sx={{
                  '& .MuiOutlinedInput-root': {
                    fontSize: '0.75rem',
                    bgcolor: 'backgroundColors.tertiary',
                    '& fieldset': {
                      borderColor: 'borderColors.lightprimary',
                    },
                    '&:hover fieldset': {
                      borderColor: 'borderColors.primary',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: 'info.main',
                    },
                  },
                }}
              />
            </Box>
          </Stack>
        </CardContent>

        {/* Footer with Action Buttons */}
        <Box
          sx={{
            px: 1.5,
            py: 1,
            bgcolor: 'backgroundColors.secondary',
            borderTop: '1px solid',
            borderColor: 'borderColors.primary',
            display: 'flex',
            gap: 1,
          }}
        >
          <Button
            variant="outlined"
            size="small"
            onClick={handleCancel}
            sx={{
              flex: 1,
              fontSize: '0.75rem',
              textTransform: 'none',
              borderColor: 'borderColors.primary',
              color: 'textColors.darkText',
              '&:hover': {
                borderColor: 'borderColors.brand',
                bgcolor: 'action.hover',
              },
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            size="small"
            onClick={handleReplace}
            sx={{
              flex: 1,
              fontSize: '0.75rem',
              textTransform: 'none',
              bgcolor: 'info.main',
              '&:hover': {
                bgcolor: 'info.dark',
              },
            }}
          >
            Replace
          </Button>
        </Box>
      </Card>
    </Popover>
  );
};

